import {
  ComponentInput,
  ComponentType,
  PositioningStrategy,
  ResponsivePosition,
} from "@/types";
import { AnyComponentProperties } from "@/types/component-properties";
import {
  isDataBindingFieldMapping,
  isDataSourceRef,
  isDataViewRef,
  isStaticDataBindingRef,
} from "@/types/componentDataBinding";
import type {
  ComponentDataBinding,
  DataBindingFieldMapping,
  DataSourceRef,
  DataViewRef,
  StaticDataBindingRef,
} from "@/types/componentDataBinding";
import { isPlainRecord, omitSanitizedRecordKeys } from "@/utils/safeRecordSanitization";
import { v4 as uuidv4 } from "uuid";

const POSITIONING_STRATEGIES: readonly PositioningStrategy[] = [
  "absolute",
  "relative",
  "grid",
  "flex",
  "auto",
];

export const COMPONENT_TYPE_SET = new Set<string>(Object.values(ComponentType));

export type TemplateComponentBlueprint = Omit<
  ComponentInput,
  "type" | "properties" | "size" | "responsivePosition"
> & {
  type: string;
  properties?: Record<string, unknown>;
  size?: {
    width?: number | string;
    height?: number | string;
  };
  responsivePosition?: Partial<Omit<ResponsivePosition, "strategy">> & {
    strategy?: string;
  };
};

export interface BindingDefaultSourceContext {
  mode: "binding";
  projectId?: string;
  pageId?: string;
  source?: unknown;
  writeTarget?: unknown;
  fieldMapping?: unknown;
  selectedOrganizationId?: string | null;
  currentOrganizationId?: string | null;
}

export interface StaticDemoDefaultSourceContext {
  mode: "static-demo";
  adapterId?: string;
  source?: unknown;
  fieldMapping?: unknown;
}

export type ComponentDefaultSourceContext =
  | BindingDefaultSourceContext
  | StaticDemoDefaultSourceContext;

export interface ComponentDefaultOptions {
  sourceContext?: ComponentDefaultSourceContext;
}

const BINDING_READY_DATA_COMPONENT_TYPES = new Set<ComponentType>([
  ComponentType.CHART,
  ComponentType.LINE_CHART,
  ComponentType.BAR_CHART,
  ComponentType.AREA_CHART,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.GANTT_CHART,
  ComponentType.CALENDAR,
  ComponentType.KANBAN_BOARD,
  ComponentType.CHECKLIST,
  ComponentType.HORIZONTAL_TIMELINE,
  ComponentType.ASSIGNMENT_WIDGET,
  ComponentType.PROJECT_HUB,
  ComponentType.TIME_TRACKER,
]);

const DURABLE_DEFAULT_RECORD_KEYS = new Set<string>([
  "assignments",
  "cards",
  "clients",
  "data",
  "events",
  "items",
  "projects",
  "sampleData",
  "tasks",
]);

const UNTRUSTED_CREATION_METADATA_KEYS = new Set<string>([
  "currentOrganizationId",
  "dataBinding",
  "dataOwnerOrganizationId",
  "dataSource",
  "dataSourceId",
  "dataSourceRef",
  "federatedViewId",
  "groupId",
  "orgId",
  "organizationId",
  "ownerId",
  "ownerType",
  "ownerOrganizationId",
  "queryTemplateId",
  "requestingOrganizationId",
  "savedViewId",
  "schemaId",
  "selectedOrganizationId",
  "source",
  "sourceContext",
  "sourceId",
  "sourceRef",
  "viewId",
  "viewOrganizationId",
  "viewRef",
  "writeBack",
  "writeSource",
  "writeTarget",
  "writeTargetId",
  "writeTargetRef",
  "writeback",
]);

function isPositioningStrategy(value: string): value is PositioningStrategy {
  return POSITIONING_STRATEGIES.some((candidate) => candidate === value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function getFieldMapping(value: unknown): DataBindingFieldMapping {
  if (value === undefined) {
    return {};
  }

  if (!isDataBindingFieldMapping(value)) {
    throw new Error("Binding-ready defaults require a valid field mapping.");
  }

  return value;
}

function getStaticDemoSource(
  context: StaticDemoDefaultSourceContext,
): StaticDataBindingRef {
  if (context.source !== undefined) {
    if (!isStaticDataBindingRef(context.source)) {
      throw new Error("Static demo defaults require a valid static source.");
    }

    return context.source;
  }

  if (!isNonEmptyString(context.adapterId)) {
    throw new Error("Static demo defaults require an adapterId.");
  }

  return {
    kind: "static",
    adapterId: context.adapterId.trim(),
  };
}

function getBindingSource(
  context: BindingDefaultSourceContext,
): DataSourceRef | DataViewRef {
  if (!isNonEmptyString(context.projectId)) {
    throw new Error("Binding-ready defaults require project context.");
  }

  if (!isNonEmptyString(context.pageId)) {
    throw new Error("Binding-ready defaults require page context.");
  }

  if (isDataSourceRef(context.source) || isDataViewRef(context.source)) {
    return context.source;
  }

  throw new Error("Binding-ready defaults require a valid source or view.");
}

function getBindingWriteTarget(
  context: BindingDefaultSourceContext,
): DataSourceRef | undefined {
  if (context.writeTarget === undefined) {
    return undefined;
  }

  if (!isDataSourceRef(context.writeTarget)) {
    throw new Error("Binding-ready defaults require a valid write target.");
  }

  return context.writeTarget;
}

function createDataBinding(
  context: ComponentDefaultSourceContext,
): ComponentDataBinding {
  if (context.mode === "static-demo") {
    return {
      source: getStaticDemoSource(context),
      fieldMapping: getFieldMapping(context.fieldMapping),
    };
  }

  const writeTarget = getBindingWriteTarget(context);
  const binding: ComponentDataBinding = {
    source: getBindingSource(context),
    fieldMapping: getFieldMapping(context.fieldMapping),
  };

  if (writeTarget === undefined) {
    return binding;
  }

  return {
    ...binding,
    writeTarget,
  };
}

function omitDurableDefaultRecords(
  properties: AnyComponentProperties,
): AnyComponentProperties {
  const bindingReadyProperties: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (!DURABLE_DEFAULT_RECORD_KEYS.has(key)) {
      bindingReadyProperties[key] = value;
    }
  }

  return bindingReadyProperties;
}

export function sanitizeUntrustedCreationProperties(
  properties: unknown,
  options?: { stripDurableRecords?: boolean },
): Record<string, unknown> {
  if (!isPlainRecord(properties)) {
    return {};
  }

  const keysToOmit = new Set(UNTRUSTED_CREATION_METADATA_KEYS);
  if (options?.stripDurableRecords === true) {
    for (const key of DURABLE_DEFAULT_RECORD_KEYS) {
      keysToOmit.add(key);
    }
  }

  return omitSanitizedRecordKeys(properties, keysToOmit);
}

function applySourceContext(
  type: ComponentType,
  properties: AnyComponentProperties,
  options?: ComponentDefaultOptions,
): AnyComponentProperties {
  const sourceContext = options?.sourceContext;
  if (sourceContext === undefined) {
    return properties;
  }

  const binding = createDataBinding(sourceContext);
  const baseProperties =
    sourceContext.mode === "binding" &&
    BINDING_READY_DATA_COMPONENT_TYPES.has(type)
      ? omitDurableDefaultRecords(properties)
      : properties;

  return {
    ...baseProperties,
    dataBinding: binding,
  };
}

export function normalizeIconName(input: string): string {
  return input
    .split(/[-_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
}

function parseSegmentsFromContent(
  content?: string,
): Array<{ id: string; label: string }> | null {
  if (!content) {
    return null;
  }

  const optionsMatch = content.match(/(?:with\s+)?options?\s+(.+)/i);
  if (!optionsMatch) {
    return null;
  }

  const raw = optionsMatch[1];
  const labels = raw.includes(",")
    ? raw.split(",").map((segment) => segment.trim()).filter(Boolean)
    : raw.split(/\s+/).filter(Boolean);

  if (labels.length < 2) {
    return null;
  }

  return labels.map((label, index) => ({ id: `seg-${index}`, label }));
}

export function isComponentType(value: string): value is ComponentType {
  return COMPONENT_TYPE_SET.has(value);
}

export function normalizeComponentType(type: string): ComponentType {
  return isComponentType(type) ? type : ComponentType.CUSTOM;
}

function getRawDefaultProperties(
  type: ComponentType,
  content?: string,
): AnyComponentProperties {
  switch (type) {
    case ComponentType.TEXT:
      return { content: content || "Text", fontSize: 16 };
    case ComponentType.DATA_TEXT:
      return {
        value: content || 0,
        label: "Value",
        showLabel: true,
        labelPosition: "above",
        valueFormatter: "number",
        fontSize: 24,
        fontWeight: 600,
        color: "#1F2937",
        labelFontSize: "12px",
        labelColor: "#6B7280",
      };
    case ComponentType.BUTTON:
      return { text: content || "Button", variant: "primary" };
    case ComponentType.INPUT:
      return { placeholder: content || "Enter text...", type: "text" };
    case ComponentType.IMAGE:
      return {
        src: content || "https://via.placeholder.com/300x200",
        alt: "Image",
      };
    case ComponentType.ICON: {
      const iconName = content ? normalizeIconName(content) : "Star";
      return {
        iconName,
        library: "lucide",
        size: 24,
        color: "#000000",
        strokeWidth: 2,
        rotation: 0,
        opacity: 1,
        label: `${iconName} icon`,
      };
    }
    case ComponentType.CARD:
      return {
        backgroundColor: "hsl(var(--card))",
        borderWidth: 1,
        borderColor: "hsl(var(--border))",
        borderStyle: "solid",
        borderRadius: 8,
        boxShadow: "0 1px 3px hsl(var(--foreground) / 0.08)",
        padding: 16,
        flexDirection: "column",
        gap: 12,
      };
    case ComponentType.FLEX_CONTAINER:
      return {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "stretch",
      };
    case ComponentType.CHART:
      return {
        chartType: "bar",
        data: getDefaultChartData(ComponentType.CHART),
        showLegend: true,
      };
    case ComponentType.FILE_STORAGE:
      return {
        title: "File Storage",
        displayMode: "grid",
        uploadEnabled: true,
        deleteEnabled: true,
        showSearch: true,
        showThumbnails: true,
        showQuotaBar: false,
        maxFileSize: 10485760,
        allowedFileTypes: ["image/*", "application/pdf"],
        defaultScope: "project",
      };
    case ComponentType.GANTT_CHART:
      return {
        ...(content ? { title: content } : {}),
        timeScale: "week",
        showTodayLine: true,
        showGridLines: true,
        showProgress: true,
        showDependencies: true,
        showMilestones: true,
        rowHeight: 36,
        barHeight: 24,
        barCornerRadius: 4,
        labelColumnWidth: 200,
        fieldMapping: {
          id: "id",
          taskName: "name",
          startDate: "start",
          endDate: "end",
          progress: "progress",
          dependencies: "deps",
          group: "group",
          milestone: "milestone",
        },
        data: [],
        sampleData: [],
      };
    case ComponentType.CALENDAR:
      return {
        ...(content ? { title: content } : {}),
        viewMode: "month",
        firstDayOfWeek: 1,
        showWeekends: true,
        showWeekNumbers: false,
        showEventTime: true,
        businessHoursStart: 9,
        businessHoursEnd: 17,
        timeSlotInterval: 60,
        maxEventsPerDay: 3,
        dragEnabled: true,
        inlineCreateEnabled: false,
        fieldMapping: {
          id: "id",
          title: "title",
          startDate: "start",
          endDate: "end",
          allDay: "allDay",
          category: "category",
          color: "color",
          description: "description",
          location: "location",
        },
        events: [],
        sampleData: [],
      };
    case ComponentType.KANBAN_BOARD:
      return {
        ...(content ? { title: content } : {}),
        columns: [
          { id: "todo", label: "To Do", color: "#6B7280" },
          { id: "in-progress", label: "In Progress", color: "#3B82F6" },
          { id: "done", label: "Done", color: "#10B981" },
        ],
        fieldMapping: {
          title: "title",
          column: "status",
          id: "id",
          priority: "priority",
          assignee: "assignee",
          sortOrder: "sortOrder",
        },
        cardLayout: "standard",
        showColumnCounts: true,
        dragEnabled: true,
        data: [],
        sampleData: [],
      };
    case ComponentType.TOGGLE_SWITCH:
      return {
        isOn: true,
        theme: "ios",
        size: "md",
      };
    case ComponentType.STATUS_LIGHT:
      return {
        status: "ok",
        blinkMode: "none",
        label: content || "Status",
        showLabel: true,
        theme: "led",
        size: "md",
        shape: "circle",
      };
    case ComponentType.RADIAL_DIAL:
      return {
        title: content || "Control",
        value: 75,
        min: 0,
        max: 100,
        step: 1,
        unit: "%",
        theme: "neon-glow",
        isOn: true,
        arcStartAngle: 135,
        arcEndAngle: 405,
      };
    case ComponentType.PUSH_BUTTON:
      return {
        label: content || "Button",
        theme: "power",
        shape: "circular",
        size: "md",
        releaseColor: "#2a2a2a",
        pressColor: "#ff3333",
        glowColor: "#ff0000",
        glowIntensity: 8,
      };
    case ComponentType.ICON_BUTTON:
      return {
        iconName: "Power",
        label: content || "",
        theme: "ghost",
        isActive: false,
        shape: "circular",
        iconSize: 24,
        showLabel: false,
      };
    case ComponentType.STEPPER:
      return {
        value: 0,
        min: 0,
        max: 100,
        step: 1,
        unit: "",
        decimals: 0,
        label: content || "",
        theme: "flat",
        size: "md",
        longPressAcceleration: true,
      };
    case ComponentType.SLIDER:
      return {
        value: 50,
        min: 0,
        max: 100,
        step: 1,
        unit: "",
        decimals: 0,
        orientation: "horizontal",
        showValue: true,
        showMinMax: false,
        showTicks: false,
        label: content || "",
        theme: "flat",
      };
    case ComponentType.LEVEL_INDICATOR:
      return {
        value: 75,
        segments: 10,
        orientation: "vertical",
        showValue: false,
        showPercentage: false,
        label: content || "",
        unit: "",
        min: 0,
        max: 100,
        theme: "battery",
      };
    case ComponentType.SEGMENTED_CONTROL:
      return {
        title: content || "Control",
        segments: parseSegmentsFromContent(content) || [
          { id: "opt1", label: "Option 1" },
          { id: "opt2", label: "Option 2" },
          { id: "opt3", label: "Option 3" },
        ],
        selectedIndex: 0,
        theme: "ios",
        size: "md",
        equalWidth: true,
      };
    case ComponentType.COLOR_PICKER:
      return {
        title: content || "Color",
        color: "#FF0000",
        mode: "wheel",
        showHex: true,
        showBrightness: false,
        theme: "compact",
      };
    case ComponentType.THERMOSTAT:
      return {
        temperature: 72,
        targetTemperature: 72,
        min: 50,
        max: 90,
        unit: "fahrenheit",
        mode: "auto",
        modes: ["auto", "heat", "cool", "fan", "off"],
        humidity: 45,
        showHumidity: true,
        theme: "nest",
      };
    case ComponentType.MODAL:
      return {
        title: content || "Modal",
        triggerText: "Open Modal",
        triggerVariant: "primary",
        size: "md",
        showBackdrop: true,
        closeOnBackdropClick: true,
        showCloseButton: true,
        animationType: "fade",
      };
    case ComponentType.GAUGE_CHART:
      return { min: 0, max: 100, value: 65, unit: "%" };
    case ComponentType.VERTICAL_GAUGE_CHART:
      return { min: 0, max: 100, value: 65, unit: "%" };
    case ComponentType.TABLE:
      return {
        columns: [
          { header: "Name", accessorKey: "name", enableSorting: true },
          { header: "Value", accessorKey: "value", enableSorting: true },
          { header: "Status", accessorKey: "status", enableSorting: true },
        ],
        data: [
          { name: "Item 1", value: 100, status: "Active" },
          { name: "Item 2", value: 200, status: "Pending" },
          { name: "Item 3", value: 150, status: "Active" },
        ],
        enableSorting: true,
        enableFiltering: true,
        enablePagination: true,
        pageSize: 10,
        striped: true,
        hoverable: true,
      };
    case ComponentType.ADVANCED_TABLE:
      return {
        columns: [
          { header: "Name", accessorKey: "name", enableSorting: true },
          { header: "Value", accessorKey: "value", enableSorting: true },
          { header: "Status", accessorKey: "status", enableSorting: true },
        ],
        data: [
          { name: "Item 1", value: 100, status: "Active" },
          { name: "Item 2", value: 200, status: "Pending" },
          { name: "Item 3", value: 150, status: "Active" },
        ],
        enableSorting: true,
        enableFiltering: true,
        enableColumnResizing: true,
        enableRowSelection: true,
        enablePagination: true,
        pageSize: 50,
        striped: true,
        hoverable: true,
      };
    case ComponentType.CHECKLIST:
      return {
        ...(content ? { title: content } : {}),
        items: [],
        showProgress: true,
        allowReorder: false,
      };
    case ComponentType.HORIZONTAL_TIMELINE:
      return {
        events: [],
        orientation: "horizontal",
        showConnectors: true,
        showDates: true,
        variant: content?.toLowerCase().includes("gantt")
          ? "gantt"
          : "milestone",
        compact: false,
      };
    case ComponentType.ASSIGNMENT_WIDGET:
      return {
        assignments: [],
        layout: "list",
        showTaskCount: true,
        showStatus: true,
      };
    case ComponentType.PROJECT_HUB:
      return {
        tabs: [
          {
            id: "table",
            label: "Table",
            componentType: "advanced-table",
            enabled: true,
          },
          {
            id: "board",
            label: "Board",
            componentType: "kanban-board",
            enabled: true,
          },
          {
            id: "timeline",
            label: "Timeline",
            componentType: "gantt-chart",
            enabled: true,
          },
          {
            id: "calendar",
            label: "Calendar",
            componentType: "calendar",
            enabled: true,
          },
        ],
        activeTab: "table",
        savedViews: [],
        fieldMapping: {
          id: "id",
          title: "title",
          status: "status",
          priority: "priority",
          assignee: "assignee",
          startDate: "startDate",
          endDate: "endDate",
          dueDate: "dueDate",
        },
        tableConfig: {
          columns: [
            { id: "title", header: "Title", accessorKey: "title", size: 250 },
            {
              id: "status",
              header: "Status",
              accessorKey: "status",
              size: 120,
            },
            {
              id: "priority",
              header: "Priority",
              accessorKey: "priority",
              size: 100,
            },
            {
              id: "assignee",
              header: "Assignee",
              accessorKey: "assignee",
              size: 120,
            },
            {
              id: "dueDate",
              header: "Due Date",
              accessorKey: "dueDate",
              size: 120,
            },
          ],
          enableSorting: true,
          striped: true,
          hoverable: true,
        },
        boardConfig: {
          columns: [
            { id: "todo", title: "To Do", color: "#6B7280" },
            { id: "in-progress", title: "In Progress", color: "#3B82F6" },
            { id: "done", title: "Done", color: "#10B981" },
          ],
          columnField: "status",
        },
        timelineConfig: {
          timeScale: "week",
          showDependencies: true,
          showProgress: true,
          showTodayLine: true,
        },
        calendarConfig: { viewMode: "month", showWeekends: true },
        sampleData: [],
      };
    case ComponentType.QUESTIONNAIRE:
      return {
        title: content || "Questionnaire",
        steps: [
          {
            id: "step-1",
            title: "Welcome",
            order: 0,
            questions: [
              {
                id: "q1",
                type: "text",
                label: "What is your name?",
                required: true,
              },
            ],
          },
          {
            id: "step-2",
            title: "Preferences",
            order: 1,
            questions: [
              {
                id: "q2",
                type: "single-select",
                label: "Pick your favorite",
                required: true,
                options: [
                  { value: "a", label: "Option A" },
                  { value: "b", label: "Option B" },
                  { value: "c", label: "Option C" },
                ],
              },
            ],
          },
          {
            id: "step-3",
            title: "Feedback",
            order: 2,
            questions: [
              {
                id: "q3",
                type: "rating",
                label: "Rate your experience",
                required: false,
              },
            ],
          },
        ],
        viewMode: "form",
        showProgress: true,
        allowSkip: false,
        allowBackNavigation: true,
        questionLayout: "stacked",
        requireAuth: false,
        allowMultipleSubmissions: false,
      };
    case ComponentType.VOTING_CARD:
      return {
        title: content || "Vote",
        description: "Cast your vote on the options below",
        options: [
          {
            id: "opt-1",
            label: "Option A",
            description: "First choice",
          },
          {
            id: "opt-2",
            label: "Option B",
            description: "Second choice",
          },
          {
            id: "opt-3",
            label: "Option C",
            description: "Third choice",
          },
        ],
        voteType: "approve-veto",
        resolution: { type: "majority", timeoutAction: "extend" },
        showResults: "after-vote",
        showVoterNames: true,
        allowChangeVote: true,
        notifyOnComplete: false,
        optionLayout: "list",
      };
    case ComponentType.TIME_TRACKER:
      return {
        viewMode: "timer",
        defaultBillable: true,
        currency: "USD",
        timeRounding: 15,
        showBillableToggle: true,
        showTags: true,
        projects: [],
        clients: [],
        tasks: [],
        data: [],
        sampleData: [],
      };
    default:
      return {};
  }
}

export function getDefaultProperties(
  type: ComponentType,
  content?: string,
  options?: ComponentDefaultOptions,
): AnyComponentProperties {
  return applySourceContext(type, getRawDefaultProperties(type, content), options);
}

export function getDefaultSize(
  type: ComponentType,
  displayMode?: string,
): { width: number; height: number } {
  switch (type) {
    case ComponentType.TEXT:
      return { width: 200, height: 50 };
    case ComponentType.DATA_TEXT:
      return { width: 150, height: 60 };
    case ComponentType.BUTTON:
      return { width: 120, height: 40 };
    case ComponentType.INPUT:
      return { width: 200, height: 40 };
    case ComponentType.IMAGE:
      return { width: 300, height: 200 };
    case ComponentType.ICON:
      return { width: 48, height: 48 };
    case ComponentType.CARD:
      return { width: 320, height: 200 };
    case ComponentType.CONTAINER:
    case ComponentType.FLEX_CONTAINER:
      return { width: 400, height: 300 };
    case ComponentType.CHART:
      return { width: 400, height: 300 };
    case ComponentType.GAUGE_CHART:
      if (displayMode === "bar") {
        return { width: 400, height: 80 };
      }
      if (displayMode === "milestone") {
        return { width: 400, height: 100 };
      }
      return { width: 200, height: 200 };
    case ComponentType.LINE_CHART:
    case ComponentType.BAR_CHART:
    case ComponentType.PIE_CHART:
    case ComponentType.SCATTER_CHART:
    case ComponentType.AREA_CHART:
    case ComponentType.HEATMAP_CHART:
    case ComponentType.TREEMAP_CHART:
    case ComponentType.VERTICAL_GAUGE_CHART:
      return { width: 400, height: 400 };
    case ComponentType.MAP_CHART:
      return { width: 500, height: 400 };
    case ComponentType.TABLE:
      return { width: 500, height: 400 };
    case ComponentType.ADVANCED_TABLE:
      return { width: 600, height: 400 };
    case ComponentType.FILE_STORAGE:
      return { width: 400, height: 300 };
    case ComponentType.GANTT_CHART:
      return { width: 900, height: 400 };
    case ComponentType.CALENDAR:
      return { width: 800, height: 600 };
    case ComponentType.KANBAN_BOARD:
      return { width: 800, height: 500 };
    case ComponentType.RADIAL_DIAL:
      return { width: 200, height: 200 };
    case ComponentType.TOGGLE_SWITCH:
      return { width: 70, height: 46 };
    case ComponentType.STATUS_LIGHT:
      return { width: 80, height: 100 };
    case ComponentType.PUSH_BUTTON:
      return { width: 80, height: 80 };
    case ComponentType.ICON_BUTTON:
      return { width: 60, height: 60 };
    case ComponentType.STEPPER:
      return { width: 200, height: 60 };
    case ComponentType.SLIDER:
      return { width: 250, height: 60 };
    case ComponentType.LEVEL_INDICATOR:
      return { width: 60, height: 150 };
    case ComponentType.SEGMENTED_CONTROL:
      return { width: 300, height: 50 };
    case ComponentType.COLOR_PICKER:
      return { width: 220, height: 220 };
    case ComponentType.THERMOSTAT:
      return { width: 260, height: 300 };
    case ComponentType.MODAL:
      return { width: 400, height: 300 };
    case ComponentType.CHECKLIST:
      return { width: 400, height: 300 };
    case ComponentType.HORIZONTAL_TIMELINE:
      return { width: 800, height: 200 };
    case ComponentType.ASSIGNMENT_WIDGET:
      return { width: 400, height: 300 };
    case ComponentType.PROJECT_HUB:
      return { width: 900, height: 600 };
    case ComponentType.QUESTIONNAIRE:
      return { width: 600, height: 500 };
    case ComponentType.VOTING_CARD:
      return { width: 500, height: 400 };
    case ComponentType.TIME_TRACKER:
      return { width: 900, height: 700 };
    default:
      return { width: 200, height: 100 };
  }
}

export function getDefaultChartData(
  type: ComponentType,
): Record<string, unknown>[] {
  const timeSeriesData = [
    { name: "Jan", value: 30 },
    { name: "Feb", value: 45 },
    { name: "Mar", value: 35 },
    { name: "Apr", value: 50 },
    { name: "May", value: 40 },
  ];

  const categoricalData = [
    { name: "Category A", value: 30 },
    { name: "Category B", value: 25 },
    { name: "Category C", value: 20 },
    { name: "Category D", value: 25 },
  ];

  const scatterData = [
    { x: 10, y: 30 },
    { x: 20, y: 45 },
    { x: 30, y: 35 },
    { x: 40, y: 50 },
    { x: 50, y: 40 },
  ];

  const heatmapData = [
    { x: "Mon", y: "Morning", value: 20 },
    { x: "Mon", y: "Afternoon", value: 35 },
    { x: "Tue", y: "Morning", value: 25 },
    { x: "Tue", y: "Afternoon", value: 40 },
    { x: "Wed", y: "Morning", value: 30 },
    { x: "Wed", y: "Afternoon", value: 45 },
  ];

  const gaugeData = [{ name: "Current", value: 65, max: 100 }];
  const mapSensorData: Array<Record<string, unknown>> = [];

  switch (type) {
    case ComponentType.PIE_CHART:
      return categoricalData;
    case ComponentType.SCATTER_CHART:
      return scatterData;
    case ComponentType.HEATMAP_CHART:
      return heatmapData;
    case ComponentType.TREEMAP_CHART:
      return categoricalData;
    case ComponentType.GAUGE_CHART:
    case ComponentType.VERTICAL_GAUGE_CHART:
      return gaugeData;
    case ComponentType.MAP_CHART:
      return mapSensorData;
    case ComponentType.LINE_CHART:
    case ComponentType.BAR_CHART:
    case ComponentType.AREA_CHART:
    default:
      return timeSeriesData;
  }
}

export function normalizeTemplateDimension(
  value: number | string | undefined,
  fallback: number,
): number | string {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    return value;
  }

  return fallback;
}

export function normalizeTemplateResponsivePosition(
  responsivePosition: TemplateComponentBlueprint["responsivePosition"],
): ResponsivePosition | undefined {
  if (!responsivePosition) {
    return undefined;
  }

  const rawStrategy = responsivePosition.strategy;
  const strategy =
    typeof rawStrategy === "string" && isPositioningStrategy(rawStrategy)
      ? rawStrategy
      : undefined;

  if (!strategy) {
    return undefined;
  }

  const { strategy: _omitted, ...rest } = responsivePosition;
  return {
    ...rest,
    strategy,
  };
}

export function getTemplateComponents(
  rawComponents: unknown,
): TemplateComponentBlueprint[] {
  if (!Array.isArray(rawComponents)) {
    return [];
  }

  return rawComponents.filter(
    (component): component is TemplateComponentBlueprint =>
      typeof component === "object" && component !== null,
  );
}

export function cloneTemplateComponents(
  templateComponents: TemplateComponentBlueprint[],
  projectId: string,
  pageId: string,
  options?: {
    includeTemplateNodeId?: boolean;
  },
): ComponentInput[] {
  const idMap = new Map<string, string>();
  const normalizedTemplateComponents = templateComponents.map((component) => ({
    ...component,
    id: component.id ?? uuidv4(),
  }));

  normalizedTemplateComponents.forEach((component) => {
    idMap.set(component.id, uuidv4());
  });

  return normalizedTemplateComponents.map((component) => {
    const clonedComponent: ComponentInput = {
      ...component,
      type: normalizeComponentType(component.type),
      id: idMap.get(component.id) ?? component.id,
      pageId,
      projectId,
      properties: options?.includeTemplateNodeId
        ? { ...(component.properties ?? {}), _templateNodeId: component.id }
        : (component.properties ?? {}),
      size: component.size
        ? {
            width: normalizeTemplateDimension(component.size.width, 300),
            height: normalizeTemplateDimension(component.size.height, 200),
          }
        : undefined,
      responsivePosition: normalizeTemplateResponsivePosition(
        component.responsivePosition,
      ),
    };

    if (component.parentId && idMap.has(component.parentId)) {
      clonedComponent.parentId = idMap.get(component.parentId);
    } else {
      delete clonedComponent.parentId;
    }

    if (Array.isArray(component.children)) {
      clonedComponent.children = component.children
        .filter(
          (childId): childId is string =>
            typeof childId === "string" && idMap.has(childId),
        )
        .map((childId) => idMap.get(childId) ?? childId);
    }

    if (options?.includeTemplateNodeId) {
      const linkedChartId = clonedComponent.properties.linkedChartId;
      if (typeof linkedChartId === "string" && idMap.has(linkedChartId)) {
        clonedComponent.properties = {
          ...clonedComponent.properties,
          linkedChartId: idMap.get(linkedChartId) ?? linkedChartId,
        };
      }
    }

    return clonedComponent;
  });
}
